/**
 * Phase 8 T-06b §20.C / §20.D · Assistant composer.
 *
 * Auto-growing textarea (up to 6 rows), emerald Send button, scope pill
 * dropdown bottom-left.
 *
 * Slash commands: typing `/` at a word boundary shows a small popup with
 * `filter | compare | draft | schedule | why`. Selecting inserts an emerald
 * outline pill (with an `×` to remove) into the composer rather than raw
 * text — so the command travels with the payload as structured metadata.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Send, X } from 'lucide-react';
import { AgentScopePill, type ScopeId } from './AgentScopePill';

const SLASH_COMMANDS = [
  { id: 'filter', label: '/filter', hint: 'narrow the result set' },
  { id: 'compare', label: '/compare', hint: 'compare two records' },
  { id: 'draft', label: '/draft', hint: 'write an email or note' },
  { id: 'schedule', label: '/schedule', hint: 'plan a meeting' },
  { id: 'why', label: '/why', hint: 'explain the reasoning' },
] as const;

export type SlashCommandId = (typeof SLASH_COMMANDS)[number]['id'];

export interface AgentComposerHandle {
  focus: () => void;
  /**
   * Phase 8 · C-48 — Replace the composer text (used by
   * `AgentQuickActions` when a chip is clicked). Re-opens the textarea
   * for editing with the cursor at end. Does not auto-send.
   */
  setText: (next: string) => void;
}

interface Props {
  scope: ScopeId;
  onScopeChange: (s: ScopeId) => void;
  onSend: (text: string, commands: SlashCommandId[]) => void;
  onStop?: () => void;
  thinking?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Phase 8 · C-48 — arbitrary node rendered in the composer's footer,
   * right-aligned next to the "Enter to send" hint. `AgentThread` uses
   * this slot for `AgentAutonomousToggle` so the autonomous control
   * lives inside the same visual surface as the Send button.
   */
  footerRight?: React.ReactNode;
}

export const AgentComposer = forwardRef<AgentComposerHandle, Props>(function AgentComposer(
  {
    scope,
    onScopeChange,
    onSend,
    onStop,
    thinking = false,
    placeholder,
    autoFocus = false,
    footerRight,
  },
  ref,
) {
  const [text, setText] = useState('');
  const [commands, setCommands] = useState<SlashCommandId[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuIndex, setMenuIndex] = useState(0);
  const [slashStart, setSlashStart] = useState<number | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  useImperativeHandle(ref, () => ({
    focus: () => taRef.current?.focus(),
    setText: (next: string) => {
      setText(next);
      // Restore focus + move caret to end after React commits.
      window.setTimeout(() => {
        const el = taRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }, 0);
    },
  }));

  useEffect(() => {
    if (autoFocus) taRef.current?.focus();
  }, [autoFocus]);

  // Auto-grow up to 6 rows
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 6 * 22; // ~22px per line
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [text]);

  const filteredCommands = useMemo(() => {
    if (!menuOpen || slashStart == null) return SLASH_COMMANDS;
    const query = text.slice(slashStart + 1).toLowerCase();
    if (!query) return SLASH_COMMANDS;
    return SLASH_COMMANDS.filter((c) => c.id.startsWith(query));
  }, [menuOpen, slashStart, text]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setSlashStart(null);
    setMenuIndex(0);
  }, []);

  const acceptCommand = useCallback(
    (cmdId: SlashCommandId) => {
      if (slashStart == null) return;
      const before = text.slice(0, slashStart);
      // Trim the "/…" partial the user was typing
      const afterCursor = text.slice(taRef.current?.selectionStart ?? slashStart);
      setText(`${before}${afterCursor}`.replace(/^\s+/, ''));
      setCommands((prev) => (prev.includes(cmdId) ? prev : [...prev, cmdId]));
      closeMenu();
      window.setTimeout(() => taRef.current?.focus(), 0);
    },
    [closeMenu, slashStart, text],
  );

  const removeCommand = (cmdId: SlashCommandId) => {
    setCommands((prev) => prev.filter((c) => c !== cmdId));
  };

  const handleChange = (next: string) => {
    setText(next);
    // Detect a fresh `/` at a word boundary
    const cursor = taRef.current?.selectionStart ?? next.length;
    const prevChar = cursor >= 2 ? next[cursor - 2] : '';
    if (next[cursor - 1] === '/' && (cursor === 1 || prevChar === ' ' || prevChar === '\n')) {
      setSlashStart(cursor - 1);
      setMenuOpen(true);
      setMenuIndex(0);
      return;
    }
    // Close the menu if the user navigates away from the slash
    if (menuOpen && slashStart != null) {
      const token = next.slice(slashStart);
      if (!token.startsWith('/') || token.includes(' ') || token.includes('\n')) {
        closeMenu();
      }
    }
  };

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed && commands.length === 0) return;
    const payload = commands.length
      ? `${commands.map((c) => `/${c}`).join(' ')} ${trimmed}`.trim()
      : trimmed;
    onSend(payload, commands);
    setText('');
    setCommands([]);
    closeMenu();
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMenuIndex((i) => (i + 1) % filteredCommands.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMenuIndex((i) => (i - 1 + filteredCommands.length) % filteredCommands.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const sel = filteredCommands[menuIndex];
        if (sel) acceptCommand(sel.id);
        return;
      }
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeMenu();
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && !menuOpen) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = Boolean(text.trim()) || commands.length > 0;

  return (
    <div
      className="relative shrink-0 border-t px-3 py-2"
      style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
    >
      {/* Command pills row (empty if no commands) */}
      {commands.length ? (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          {commands.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1 rounded-[10px] border px-2 py-0.5 text-[11px] font-medium"
              style={{
                borderColor:
                  'color-mix(in srgb, var(--ggj-accent,#10B981) 35%, var(--vscode-border-primary))',
                color: 'var(--ggj-accent,#10B981)',
                background: 'var(--ggj-accent-soft,#D1FAE5)',
              }}
            >
              /{c}
              <button
                type="button"
                onClick={() => removeCommand(c)}
                className="rounded-full p-0.5 transition-colors hover:bg-[color-mix(in_srgb,var(--ggj-accent,#10B981)_20%,transparent)]"
                aria-label={`Remove /${c}`}
              >
                <X className="h-2.5 w-2.5" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {/*
       * Post user-feedback (21-Apr): simplified composer.
       *
       * The textarea is now the chrome — a single bordered box that
       * contains the input AND the Send button in its bottom-right
       * corner (ChatGPT-style). The old "Enter to send · Shift+Enter
       * for newline" hint was dropped from the footer (moved to the
       * textarea `title` tooltip) and the verbose placeholder was
       * shortened to "Message…" so the control reads cleanly at a
       * glance. The scope pill + autonomous toggle sit below as a
       * tiny utility row with reduced visual weight.
       */}
      {/*
       * Post user-feedback (21-Apr) — the textarea was reported as
       * "invisible while typing" in some themes. The composer's parent
       * chain used `background: var(--vscode-bg-elevated)` but the
       * textarea itself was `bg-transparent`; when the composer is
       * dropped into a non-elevated surface (e.g. Smart Match intake
       * which nests the composer under a primary-bg wrapper) the
       * inherited background disappears and the text can land on a
       * surface whose contrast with `--vscode-text-primary` is too
       * low. We now pin both the background and the foreground on the
       * textarea wrapper AND on the textarea itself so the control is
       * always readable.
       */}
      <div
        className="relative rounded-[12px] border-[1.5px] transition-colors focus-within:border-[color-mix(in_srgb,var(--ggj-accent,#10B981)_55%,var(--vscode-border-primary))] focus-within:ring-2 focus-within:ring-[color-mix(in_srgb,var(--ggj-accent,#10B981)_35%,transparent)]"
        style={{
          borderColor: 'color-mix(in srgb, var(--ggj-accent,#10B981) 18%, var(--vscode-border-primary))',
          background: 'var(--vscode-bg-elevated)',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px -8px rgba(15, 23, 42, 0.12)',
        }}
      >
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder ?? 'Message…'}
          title="Enter to send · Shift+Enter for newline"
          rows={1}
          className="block w-full resize-none px-3 py-2 pr-11 text-[13px] leading-[22px] outline-none"
          style={{
            minHeight: 38,
            color: 'var(--vscode-text-primary)',
            background: 'transparent',
            caretColor: 'var(--ggj-accent,#10B981)',
          }}
          aria-label="Assistant prompt"
        />
        {thinking && onStop ? (
          <button
            type="button"
            onClick={onStop}
            className="absolute bottom-1.5 right-1.5 inline-flex h-7 w-7 items-center justify-center rounded-[8px] border transition-colors"
            style={{
              borderColor: 'color-mix(in srgb, #EF4444 40%, var(--vscode-border-primary))',
              color: '#EF4444',
            }}
            aria-label="Stop"
            title="Stop"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="absolute bottom-1.5 right-1.5 inline-flex h-7 w-7 items-center justify-center rounded-[8px] text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-30"
            style={{ background: 'var(--ggj-accent,#10B981)' }}
            aria-label="Send prompt"
            title="Send (Enter)"
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <AgentScopePill value={scope} onChange={onScopeChange} />
        {footerRight ?? null}
      </div>

      {menuOpen && filteredCommands.length > 0 ? (
        <div
          role="listbox"
          aria-label="Slash commands"
          className="absolute left-3 bottom-full mb-1 z-30 w-[260px] rounded-[10px] border py-1 shadow-lg"
          style={{
            background: 'var(--vscode-bg-elevated)',
            borderColor: 'var(--vscode-border-primary)',
          }}
        >
          {filteredCommands.map((c, i) => {
            const active = i === menuIndex;
            return (
              <button
                type="button"
                role="option"
                aria-selected={active}
                key={c.id}
                onMouseEnter={() => setMenuIndex(i)}
                onClick={() => acceptCommand(c.id)}
                className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-[12px]"
                style={{
                  background: active ? 'var(--vscode-bg-hover)' : 'transparent',
                  color: 'var(--vscode-text-primary)',
                }}
              >
                <span className="font-mono" style={{ color: 'var(--ggj-accent,#10B981)' }}>
                  {c.label}
                </span>
                <span style={{ color: 'var(--vscode-text-tertiary)' }}>{c.hint}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
});
